import React, { useState, useEffect, useRef } from 'react';
import { Check, ArrowRight, ArrowLeft, Calendar, Book, User, Package, Feather } from 'lucide-react';

const ContactForm = () => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  const initialFormState = {
    package: '',
    name: '',
    email: '',
    phone: '',
    bookTitle: '',
    genre: '',
    description: '',
    meetingDate: '',
    meetingTime: ''
  };

  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState(initialFormState);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const TELEGRAM_BOT_TOKEN = '7792080768:AAGdZ6jv4H4ueprl4aPJpgXRSmuGaxfxcxo';
  const TELEGRAM_CHAT_ID = '1523610682';

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const steps = [
    { number: 1, title: "Package", icon: <Package className="w-5 h-5" /> },
    { number: 2, title: "Details", icon: <User className="w-5 h-5" /> },
    { number: 3, title: "Your Book", icon: <Book className="w-5 h-5" /> },
    { number: 4, title: "Schedule", icon: <Calendar className="w-5 h-5" /> }
  ];

  const packages = [
    { id: 'essential', name: 'Essential', price: '$799', description: 'Perfect for emerging authors' },
    { id: 'professional', name: 'Professional', price: '$1,499', description: 'For established authors', popular: true },
    { id: 'premium', name: 'Premium', price: '$2,999', description: 'Complete custom solution' }
  ];

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const resetForm = () => {
    setFormData(initialFormState);
    setCurrentStep(1);
  };

  const sendToTelegram = async (formData) => {
    const message = `📬 New Inquiry from WriteToSite.com\n\n` +
      `📦 Package: ${formData.package}\n` +
      `👤 Name: ${formData.name}\n` +
      `📧 Email: ${formData.email}\n` +
      `📱 Phone: ${formData.phone}\n` +
      `📚 Book Title: ${formData.bookTitle}\n` +
      `📖 Genre: ${formData.genre}\n` +
      `📝 Description: ${formData.description}\n` +
      `📅 Meeting Date: ${formData.meetingDate}\n` +
      `⏰ Meeting Time: ${formData.meetingTime}`;

    try {
      const response = await fetch(`https://api.telegram.org/bot${TELEGRAM_BOT_TOKEN}/sendMessage`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          chat_id: TELEGRAM_CHAT_ID,
          text: message,
          parse_mode: 'HTML'
        })
      });

      if (!response.ok) {
        throw new Error('Failed to send message');
      }
    } catch (error) {
      console.error('Error:', error);
      throw error;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await sendToTelegram(formData);
      alert('Thank you! We\'ll be in touch soon to discuss your author website.');
      resetForm();
    } catch (error) {
      alert('There was an error. Please try again or email us directly.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const nextStep = () => setCurrentStep(curr => Math.min(curr + 1, 4));
  const prevStep = () => setCurrentStep(curr => Math.max(curr - 1, 1));

  return (
    <section ref={sectionRef} className="relative py-24 lg:py-32 bg-ink-900 overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0">
        <div className="absolute inset-0 opacity-[0.02]" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
        }} />
        <div className="absolute top-0 right-1/4 w-96 h-96 bg-gold-500/5 rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-1/4 w-[500px] h-[500px] bg-gold-400/5 rounded-full blur-3xl" />
      </div>

      {/* Gold Lines */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-gold-500/30 to-transparent" />

      <div className="container mx-auto px-6 lg:px-12 relative z-10">
        {/* Header */}
        <div className={`text-center max-w-3xl mx-auto mb-16 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div className="inline-flex items-center gap-3 mb-6">
            <span className="w-8 h-px bg-gold-500" />
            <span className="text-gold-400 text-sm font-sans tracking-[0.2em] uppercase">
              Get Started
            </span>
            <span className="w-8 h-px bg-gold-500" />
          </div>

          <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold text-cream-50 mb-6">
            Begin Your
            <span className="block mt-2 text-gradient-gold">Author Journey</span>
          </h2>

          <p className="font-body text-lg text-cream-300 leading-relaxed">
            Tell us about your vision and we'll craft the perfect digital home for your literary work.
          </p>
        </div>

        {/* Form Container */}
        <div className={`max-w-3xl mx-auto transition-all duration-1000 delay-200 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          {/* Progress Steps */}
          <div className="flex items-center justify-between mb-12 relative">
            {/* Connector Line */}
            <div className="absolute top-5 left-0 right-0 h-px bg-ink-700" />
            <div
              className="absolute top-5 left-0 h-px bg-gradient-to-r from-gold-500 to-gold-600 transition-all duration-500"
              style={{ width: `${((currentStep - 1) / 3) * 100}%` }}
            />

            {steps.map((step, index) => (
              <div key={index} className="relative flex flex-col items-center z-10">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center transition-all duration-300 ${
                  currentStep > step.number
                    ? 'bg-gold-500 text-ink-900'
                    : currentStep === step.number
                    ? 'bg-ink-700 text-gold-400 ring-2 ring-gold-500'
                    : 'bg-ink-800 text-ink-500'
                }`}>
                  {currentStep > step.number ? <Check className="w-5 h-5" /> : step.icon}
                </div>
                <span className={`mt-2 text-sm font-sans transition-colors duration-300 ${
                  currentStep >= step.number ? 'text-cream-200' : 'text-ink-500'
                }`}>
                  {step.title}
                </span>
              </div>
            ))}
          </div>

          {/* Form Card */}
          <div className="bg-ink-800/50 border border-ink-700 backdrop-blur-sm p-8 lg:p-12">
            <form onSubmit={handleSubmit}>
              {/* Step 1: Package Selection */}
              {currentStep === 1 && (
                <div className="space-y-6">
                  <h3 className="font-display text-2xl text-cream-50 mb-8">Select Your Package</h3>
                  <div className="grid gap-4">
                    {packages.map((pkg) => (
                      <label
                        key={pkg.id}
                        className={`relative p-6 border cursor-pointer transition-all duration-300 ${
                          formData.package === pkg.id
                            ? 'border-gold-500 bg-ink-800'
                            : 'border-ink-600 hover:border-ink-500'
                        }`}
                      >
                        <input
                          type="radio"
                          name="package"
                          value={pkg.id}
                          className="hidden"
                          onChange={handleInputChange}
                          checked={formData.package === pkg.id}
                        />
                        <div className="flex items-center justify-between">
                          <div>
                            <div className="flex items-center gap-3">
                              <span className="font-display text-xl text-cream-50">{pkg.name}</span>
                              {pkg.popular && (
                                <span className="px-2 py-1 bg-gold-500/20 text-gold-400 text-xs font-sans tracking-wide rounded">
                                  Popular
                                </span>
                              )}
                            </div>
                            <p className="text-cream-400 text-sm mt-1">{pkg.description}</p>
                          </div>
                          <div className="flex items-center gap-3">
                            <span className="font-display text-2xl text-gold-400">{pkg.price}</span>
                            <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-colors ${
                              formData.package === pkg.id
                                ? 'border-gold-500 bg-gold-500'
                                : 'border-ink-500'
                            }`}>
                              {formData.package === pkg.id && <Check className="w-4 h-4 text-ink-900" />}
                            </div>
                          </div>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>
              )}

              {/* Step 2: Contact Information */}
              {currentStep === 2 && (
                <div className="space-y-6">
                  <h3 className="font-display text-2xl text-cream-50 mb-8">Your Contact Information</h3>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-cream-300 font-sans text-sm mb-2">Full Name</label>
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        className="input-dark"
                        placeholder="Your name"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-cream-300 font-sans text-sm mb-2">Email Address</label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        className="input-dark"
                        placeholder="you@example.com"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-cream-300 font-sans text-sm mb-2">Phone Number</label>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        className="input-dark"
                        placeholder="+1 (555) 000-0000"
                        required
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Step 3: Book Information */}
              {currentStep === 3 && (
                <div className="space-y-6">
                  <h3 className="font-display text-2xl text-cream-50 mb-8">Tell Us About Your Book</h3>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-cream-300 font-sans text-sm mb-2">Book Title</label>
                      <input
                        type="text"
                        name="bookTitle"
                        value={formData.bookTitle}
                        onChange={handleInputChange}
                        className="input-dark"
                        placeholder="Your book's title"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-cream-300 font-sans text-sm mb-2">Genre</label>
                      <select
                        name="genre"
                        value={formData.genre}
                        onChange={handleInputChange}
                        className="input-dark"
                        required
                      >
                        <option value="">Select a genre</option>
                        <option value="fiction">Fiction</option>
                        <option value="non-fiction">Non-Fiction</option>
                        <option value="mystery">Mystery & Thriller</option>
                        <option value="romance">Romance</option>
                        <option value="sci-fi">Science Fiction</option>
                        <option value="fantasy">Fantasy</option>
                        <option value="self-help">Self-Help</option>
                        <option value="children">Children's Books</option>
                        <option value="other">Other</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-cream-300 font-sans text-sm mb-2">Brief Description</label>
                      <textarea
                        name="description"
                        value={formData.description}
                        onChange={handleInputChange}
                        className="input-dark min-h-[120px] resize-none"
                        placeholder="Tell us about your book and what you envision for your author website..."
                        required
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Step 4: Schedule Meeting */}
              {currentStep === 4 && (
                <div className="space-y-6">
                  <h3 className="font-display text-2xl text-cream-50 mb-8">Schedule Your Consultation</h3>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-cream-300 font-sans text-sm mb-2">Preferred Date</label>
                      <input
                        type="date"
                        name="meetingDate"
                        value={formData.meetingDate}
                        onChange={handleInputChange}
                        className="input-dark"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-cream-300 font-sans text-sm mb-2">Preferred Time (EST)</label>
                      <select
                        name="meetingTime"
                        value={formData.meetingTime}
                        onChange={handleInputChange}
                        className="input-dark"
                        required
                      >
                        <option value="">Select a time</option>
                        <option value="9:00">9:00 AM</option>
                        <option value="10:00">10:00 AM</option>
                        <option value="11:00">11:00 AM</option>
                        <option value="13:00">1:00 PM</option>
                        <option value="14:00">2:00 PM</option>
                        <option value="15:00">3:00 PM</option>
                        <option value="16:00">4:00 PM</option>
                      </select>
                    </div>
                  </div>

                  {/* Summary */}
                  <div className="mt-8 p-6 bg-ink-900/50 border border-ink-700">
                    <h4 className="font-display text-lg text-cream-50 mb-4">Summary</h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-cream-400">Package:</span>
                        <span className="text-cream-200 capitalize">{formData.package || 'Not selected'}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-cream-400">Name:</span>
                        <span className="text-cream-200">{formData.name || 'Not provided'}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-cream-400">Book:</span>
                        <span className="text-cream-200">{formData.bookTitle || 'Not provided'}</span>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Navigation Buttons */}
              <div className="flex justify-between mt-10 pt-8 border-t border-ink-700">
                {currentStep > 1 ? (
                  <button
                    type="button"
                    onClick={prevStep}
                    className="flex items-center gap-2 px-6 py-3 text-cream-300 hover:text-cream-50 transition-colors font-sans"
                  >
                    <ArrowLeft className="w-4 h-4" />
                    Back
                  </button>
                ) : (
                  <div />
                )}

                {currentStep < 4 ? (
                  <button
                    type="button"
                    onClick={nextStep}
                    className="btn-gold"
                  >
                    <span className="flex items-center gap-2">
                      Continue
                      <ArrowRight className="w-4 h-4" />
                    </span>
                  </button>
                ) : (
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="btn-gold disabled:opacity-50"
                  >
                    <span className="flex items-center gap-2">
                      {isSubmitting ? 'Submitting...' : 'Submit Request'}
                      <Feather className="w-4 h-4" />
                    </span>
                  </button>
                )}
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactForm;
